import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Region } from '../types';

interface BalanceEntry {
  region: Region;
  amount: number;
  currency: string;
}

interface UserStore {
  user: User | null;
  blocked: boolean;
  lastSelectedRegion: Region | null;
  balances: BalanceEntry[];
  /** Последний известный бонусный баланс — чтобы при открытии не мигал ноль */
  cachedBonus: number | null;
  setUser: (user: User) => void;
  setBlocked: (blocked: boolean) => void;
  setLastSelectedRegion: (region: Region) => void;
  addBalance: (region: Region, amount: number, currency: string) => void;
  setCachedBonus: (amount: number) => void;
  getBalance: (region: Region) => number;
}

const REGION_KEY = 'ps_store_region';

// Стратегия: храним регион в localStorage, но при свежем открытии mini app
// (navigation.type === 'navigate') стираем его, чтобы RegionGuard показал
// экран выбора. При reload (pull-to-refresh / location.reload) значение
// сохраняется — контекст переживает перезагрузку.
function isFreshOpen(): boolean {
  try {
    if (typeof window === 'undefined' || !window.performance) return true;
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const navType = entries[0]?.type;
    // 'navigate' = свежий переход/открытие; 'reload' = перезагрузка страницы
    return navType !== 'reload' && navType !== 'back_forward';
  } catch {
    return true;
  }
}

function loadRegion(): Region | null {
  try {
    if (typeof window === 'undefined') return null;
    if (isFreshOpen()) {
      localStorage.removeItem(REGION_KEY);
      return null;
    }
    const v = localStorage.getItem(REGION_KEY);
    if (v === 'TR' || v === 'IN' || v === 'UA') return v;
  } catch { /* ignore */ }
  return null;
}

function saveRegion(region: Region) {
  try {
    if (typeof window !== 'undefined') localStorage.setItem(REGION_KEY, region);
  } catch { /* ignore */ }
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      user: null,
      blocked: false,
      lastSelectedRegion: loadRegion(),
      balances: [],
      cachedBonus: null,

      setUser: (user) => {
        set({ user });
      },

      setBlocked: (blocked) => {
        set({ blocked });
      },

      setCachedBonus: (amount) => set({ cachedBonus: amount }),
      setLastSelectedRegion: (region) => {
        saveRegion(region);
        set({ lastSelectedRegion: region });
      },

      addBalance: (region, amount, currency) => {
        const balances = [...get().balances];
        const existing = balances.find((b) => b.region === region);
        if (existing) {
          existing.amount += amount;
        } else {
          balances.push({ region, amount, currency });
        }
        set({ balances });
      },

      getBalance: (region) => {
        const entry = get().balances.find((b) => b.region === region);
        return entry?.amount ?? 0;
      },
    }),
    {
      name: 'user-storage',
      partialize: (s) => ({ user: s.user, balances: s.balances, cachedBonus: s.cachedBonus }),
    }
  )
);
